import { useCallback, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useProfessionalAuth } from "../../../store/authProfessionalStore";
import {
  acceptConsultationAssignment,
  getAssignedConsultationsForProfessionnel,
  refuseConsultationAssignment,
  type Consultation,
} from "../../../api/consultations";
import { showToast } from "../../../components/ui/toastHotSonner";

const PAGE_SIZE = 20;

const STATUT_LABELS: Record<string, { label: string; className: string }> = {
  en_attente: { label: "En attente de votre réponse", className: "bg-amber-100 text-amber-900" },
  acceptee: { label: "Acceptée", className: "bg-[#DCEFE9] text-[#00553E]" },
  refusee: { label: "Refusée", className: "bg-red-100 text-red-700" },
};

function formatDateFR(input?: string | null): string {
  if (!input) return "";
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return String(input);
  return d.toLocaleString("fr-FR");
}

export default function MesConsultationsAssignees() {
  const { professional } = useProfessionalAuth();
  const proNip = professional?.nip?.trim();

  const [items, setItems] = useState<Consultation[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [refuseTarget, setRefuseTarget] = useState<Consultation | null>(null);
  const [refuseMotif, setRefuseMotif] = useState("");
  const [refusing, setRefusing] = useState(false);

  const load = useCallback(async () => {
    if (!proNip) {
      setLoading(false);
      setError("NIP professionnel indisponible.");
      return;
    }
    setLoading(true);
    setError(null);
    const res = await getAssignedConsultationsForProfessionnel(proNip, page, PAGE_SIZE);
    if (!res.ok) {
      setError(res.message);
      setLoading(false);
      return;
    }
    setItems(res.data);
    setTotalPages(res.pagination.totalPages || 1);
    setLoading(false);
  }, [proNip, page]);

  useEffect(() => { void load(); }, [load]);

  const accept = async (c: Consultation) => {
    setActingId(c.id);
    const res = await acceptConsultationAssignment(c.id);
    setActingId(null);
    if (!res.ok) {
      showToast(res.message, "error");
      return;
    }
    showToast("Consultation acceptée. Vous pouvez maintenant la traiter depuis la fiche patient.", "success");
    void load();
  };

  const confirmRefuse = async () => {
    if (!refuseTarget) return;
    setRefusing(true);
    const res = await refuseConsultationAssignment(refuseTarget.id, refuseMotif.trim() || undefined);
    setRefusing(false);
    if (!res.ok) {
      showToast(res.message, "error");
      return;
    }
    setRefuseTarget(null);
    setRefuseMotif("");
    showToast("Consultation refusée.", "success");
    void load();
  };

  return (
    <div>
      <div className="text-white">
        <h1 className="text-3xl md:text-4xl font-bold">Consultations assignées</h1>
        <p className="mt-2 max-w-2xl text-sm md:text-base text-white/90">
          Consultations créées par un établissement et qui vous ont été assignées. Acceptez pour les traiter, ou refusez si
          vous n'êtes pas disponible.
        </p>
      </div>

      <div
        className="mt-6 rounded-[10px] p-6 text-[#0B1F16]"
        style={{
          background: "linear-gradient(180deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.72) 100%)",
          backdropFilter: "blur(6px)",
          border: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        {loading && <p className="text-sm text-[#0B1F16]/60 py-6 text-center">Chargement…</p>}

        {!loading && error && (
          <div className="rounded-[8px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            {error}
            <button type="button" onClick={() => void load()} className="ml-3 font-semibold underline">
              Réessayer
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <p className="text-sm text-[#0B1F16]/60 py-6 text-center">Aucune consultation assignée pour le moment.</p>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="space-y-3">
            {items.map((c) => {
              const statutMeta = STATUT_LABELS[c.statutAssignation] ?? STATUT_LABELS.en_attente;
              return (
                <div
                  key={c.id}
                  className="rounded-[8px] p-4 bg-[#E8F4EF]/80 border border-[#CDE5DB] flex flex-col gap-2 md:flex-row md:items-center md:justify-between"
                >
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#00553E]">Patient NIP {c.patientNip}</span>
                      <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statutMeta.className}`}>
                        {statutMeta.label}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-[#0B1F16]/80">{c.motif}</div>
                    <div className="mt-1 text-xs text-[#0B1F16]/50">Assignée le {formatDateFR(c.dateAssignation ?? c.dateCreation)}</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {c.statutAssignation === "en_attente" && (
                      <>
                        <button
                          type="button"
                          disabled={actingId === c.id}
                          onClick={() => void accept(c)}
                          className="h-9 px-4 rounded-[8px] font-semibold text-white text-sm disabled:opacity-60"
                          style={{ background: "#00553E" }}
                        >
                          {actingId === c.id ? "…" : "Accepter"}
                        </button>
                        <button
                          type="button"
                          disabled={actingId === c.id}
                          onClick={() => { setRefuseTarget(c); setRefuseMotif(""); }}
                          className="h-9 px-4 rounded-[8px] border text-sm text-red-600 disabled:opacity-60"
                          style={{ borderColor: "#F3C0C0" }}
                        >
                          Refuser
                        </button>
                      </>
                    )}
                    {c.statutAssignation === "acceptee" && (
                      <NavLink
                        to={`/pro/dashboard/patients/${encodeURIComponent(c.patientNip)}`}
                        className="h-9 px-4 rounded-[8px] font-semibold text-white text-sm inline-flex items-center"
                        style={{ background: "#00553E" }}
                      >
                        Ouvrir la fiche patient
                      </NavLink>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t text-sm" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="disabled:opacity-40 font-medium"
              style={{ color: "#00553E" }}
            >
              ← Précédent
            </button>
            <span className="text-[#0B1F16]/60">Page {page} / {totalPages}</span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="disabled:opacity-40 font-medium"
              style={{ color: "#00553E" }}
            >
              Suivant →
            </button>
          </div>
        )}
      </div>

      {refuseTarget && (
        <div className="fixed inset-0 z-20">
          <div className="absolute inset-0 bg-black/40" onClick={() => { if (!refusing) setRefuseTarget(null); }} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-[12px] p-6 w-full max-w-md shadow">
              <div className="text-lg font-semibold text-[#0B1F16]">
                Refuser la consultation de NIP {refuseTarget.patientNip} ?
              </div>
              <p className="mt-2 text-sm text-gray-600">
                L'établissement en sera notifié et pourra réassigner cette consultation à un autre membre de son personnel.
              </p>
              <div className="mt-4 space-y-1">
                <label className="text-sm font-medium text-[#0B1F16]">Motif (optionnel)</label>
                <textarea
                  value={refuseMotif}
                  onChange={(e) => setRefuseMotif(e.target.value)}
                  placeholder="Ex. indisponible sur ce créneau"
                  className="w-full min-h-[80px] rounded-[10px] border px-3 pt-2 text-sm"
                  style={{ borderColor: "#E5E7EB" }}
                  disabled={refusing}
                />
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  className="h-11 rounded-[10px] font-semibold text-white disabled:opacity-60"
                  style={{ background: "#B91C1C" }}
                  disabled={refusing}
                  onClick={() => void confirmRefuse()}
                >
                  {refusing ? "Refus…" : "Confirmer le refus"}
                </button>
                <button
                  type="button"
                  className="h-11 rounded-[10px] font-semibold border"
                  style={{ borderColor: "#00553E", color: "#00553E", background: "white" }}
                  onClick={() => setRefuseTarget(null)}
                  disabled={refusing}
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
